import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Alert, Button, Spinner} from 'react-bootstrap';
import {FaChartBar, FaExternalLinkAlt, FaShareAlt, FaTrashAlt} from 'react-icons/fa';
import {useTranslation} from 'react-i18next';
import Configuration from '../config';
import * as api from '../api';
import PlayerVideoAnalysisSharing from './player-video-analysis-sharing';

const statusLabel = (status, t) => t(({
  created: 'VIDEO_ANALYSIS_STATUS_CREATED',
  payment_confirmed: 'VIDEO_ANALYSIS_STATUS_CONFIRMED',
  waiting_video_slices: 'VIDEO_ANALYSIS_STATUS_WAITING_VIDEO',
  clip_generating: 'VIDEO_ANALYSIS_STATUS_CLIP',
  queued_to_provider: 'VIDEO_ANALYSIS_STATUS_QUEUED',
  processing: 'VIDEO_ANALYSIS_STATUS_PROCESSING',
  available: 'VIDEO_ANALYSIS_STATUS_AVAILABLE',
  failed: 'VIDEO_ANALYSIS_STATUS_FAILED',
  refunded: 'VIDEO_ANALYSIS_STATUS_REFUNDED'
}[status] || 'VIDEO_ANALYSIS_STATUS_DEFAULT'));
const statusDescription = (status, t) => t(({
  created: 'VIDEO_ANALYSIS_DESCRIPTION_CREATED',
  payment_confirmed: 'VIDEO_ANALYSIS_DESCRIPTION_CONFIRMED',
  waiting_video_slices: 'VIDEO_ANALYSIS_DESCRIPTION_WAITING_VIDEO',
  clip_generating: 'VIDEO_ANALYSIS_DESCRIPTION_CLIP',
  queued_to_provider: 'VIDEO_ANALYSIS_DESCRIPTION_QUEUED',
  processing: 'VIDEO_ANALYSIS_DESCRIPTION_PROCESSING',
  available: 'VIDEO_ANALYSIS_DESCRIPTION_AVAILABLE',
  failed: 'VIDEO_ANALYSIS_DESCRIPTION_FAILED',
  refunded: 'VIDEO_ANALYSIS_DESCRIPTION_REFUNDED'
}[status] || 'VIDEO_ANALYSIS_DESCRIPTION_DEFAULT'));
const resultUrl = request => request.providerResultUrl || request.provider_result_url || request.providerWebpageUrl || request.provider_webpage_url || request.webpageUrl || request.webpage || null;
const formatRange = request => {
  const format = value => new Date(Math.max(0, Number(value) || 0) * 1000).toISOString().slice(11, 19);
  return `${format(request.startSecond || request.start_second)} - ${format(request.endSecond || request.end_second)}`;
};
const formatPrice = request => `${(Number(request.priceCents || request.price_cents || 0) / 100).toFixed(2)} EUR`;

const VideoAnalysisSection = ({token, goto, reportPathPrefix}) => {
  const {t, i18n} = useTranslation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [sharingRequest, setSharingRequest] = useState(null);
  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    api.getVideoAnalysisRequests(token).then(result => {
      setLoading(false);
      if (result.status >= 200 && result.status < 300) setRequests(Array.isArray(result.json) ? result.json : result.json.requests || result.json.items || []);
      else setError(t('VIDEO_ANALYSIS_LOAD_ERROR'));
    });
  }, [token, t]);
  useEffect(() => { load(); }, [load]);
  const remove = request => async event => {
    event.preventDefault();
    setDeletingId(request.id);
    const result = await api.deleteVideoAnalysisRequest(request.id, token);
    setDeletingId(null);
    if (result.status >= 200 && result.status < 300) load();
    else setError(t(result.json && result.json.error === 'analysis_already_sent_to_provider' ? 'VIDEO_ANALYSIS_DELETE_PROCESSING_ERROR' : 'VIDEO_ANALYSIS_DELETE_ERROR'));
  };
  const updateShared = updated => {
    setRequests(items => items.map(item => item.id === updated.id ? {...item, ...updated} : item));
    setSharingRequest(updated);
  };
  const replayerUi = Configuration.videoAnalysisReplayerUiEnabled !== false;
  return <React.Fragment>
    <div className="myvideo-highlight-organizer" style={{marginTop: 35}}><div className="myvideo-highlight-organizer-header"><div><h5>{t('VIDEO_ANALYSIS_TITLE')}</h5><p>{t('VIDEO_ANALYSIS_INTRO')}</p></div><div className="myvideo-highlight-header-actions"><div className="myvideo-highlight-total">{t('VIDEO_ANALYSIS_COUNT', {count: requests.length})}</div></div></div></div>
    {loading ? <div className="d-flex align-items-center" style={{marginTop: 35}}><Spinner animation="border" size="sm" className="mr-2"/>{t('VIDEO_ANALYSIS_LOADING')}</div> : null}
    {error ? <Alert variant="danger" style={{marginTop: 35}}>{error}</Alert> : null}
    <div className="row" style={{marginTop: 50}}>
      {!loading && requests.length === 0 ? <div className="col-12"><div className="myvideo-highlight-empty">{t('VIDEO_ANALYSIS_EMPTY')}</div></div> : null}
      {requests.map(request => {
        const url = resultUrl(request);
        const status = request.status || request.providerStatus || request.provider_status;
        return <div key={request.id} className="col-md-12 col-lg-4 mb-sm-4 card-group"><div className="card video_card_bg_rp skywork-analysis-card"><div className="card-body d-flex flex-column">
          <div className="skywork-video-card-title-box"><h5 className="card-title">{request.title || t('VIDEO_ANALYSIS_DEFAULT_TITLE')}</h5></div><div className="skywork-video-card-description-box">{request.description || t('VIDEO_ANALYSIS_NO_DESCRIPTION')}</div>
          <div className={`skywork-analysis-status-box skywork-analysis-status-box--${status || 'unknown'}`}><div className="skywork-analysis-status-heading"><FaChartBar/><span>{statusLabel(status, t)}</span></div><ul className="list-unstyled"><li>{statusDescription(status, t)}</li><li><strong>{t('VIDEO_ANALYSIS_CLIP')}:</strong> {formatRange(request)}</li><li><strong>{t('VIDEO_ANALYSIS_COST')}:</strong> {formatPrice(request)}</li>{request.errorReason ? <li><strong>{t('VIDEO_ANALYSIS_ERROR')}:</strong> {request.errorReason}</li> : null}{request.createdAt || request.created_at ? <li><strong>{t('VIDEO_ANALYSIS_REQUESTED')}:</strong> {new Date(request.createdAt || request.created_at).toLocaleString(i18n.language)}</li> : null}</ul></div>
          <div className="text-center mt-auto">{status === 'available' && replayerUi ? <Button className="btn myvideo-action myvideo-action--analysis mb-2" onClick={goto(`${reportPathPrefix}/${request.id}`)}><FaChartBar/>{t('VIDEO_ANALYSIS_OPEN_REPLAYER')}</Button> : url ? <a href={url} target="_blank" rel="noopener noreferrer" className="btn myvideo-action myvideo-action--analysis mb-2"><FaExternalLinkAlt/>{t('VIDEO_ANALYSIS_OPEN')}</a> : <Button className="btn myvideo-action myvideo-action--utility mb-2" disabled>{statusLabel(status, t)}</Button>}{status === 'available' && replayerUi && url ? <a href={url} target="_blank" rel="noopener noreferrer" className="btn myvideo-action myvideo-action--external mb-2 ml-2"><FaExternalLinkAlt/>{t('VIDEO_ANALYSIS_OPEN_PB')}</a> : null}{status === 'available' && replayerUi ? <Button className="btn myvideo-action myvideo-action--share mb-2 ml-2" onClick={event => {event.preventDefault(); setSharingRequest(request);}}><FaShareAlt/>{t('SHARE')}</Button> : null}{!['queued_to_provider', 'processing'].includes(status) ? <Button className="btn myvideo-action myvideo-action--danger mb-2 ml-2" onClick={remove(request)} disabled={deletingId === request.id}><FaTrashAlt/>{t(deletingId === request.id ? 'VIDEO_ANALYSIS_DELETING' : 'DELETE')}</Button> : null}</div>
        </div></div></div>;
      })}
    </div>
    <PlayerVideoAnalysisSharing
      show={!!sharingRequest}
      request={sharingRequest}
      token={token}
      onHide={() => setSharingRequest(null)}
      onShared={updateShared}
    />
  </React.Fragment>;
};
VideoAnalysisSection.propTypes = {token: PropTypes.string, goto: PropTypes.func.isRequired, reportPathPrefix: PropTypes.string};
VideoAnalysisSection.defaultProps = {token: null, reportPathPrefix: '/player-video-analysis'};
export default VideoAnalysisSection;
